import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
} from "reactstrap";
import { AlertCircle, CheckCircle, Building2, MapPin, Phone, Mail, Globe, FileText, Tag } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const ORGANIZATION_TYPES = [
  { value: "", label: "Select type..." },
  { value: "Nonprofit", label: "Nonprofit" },
  { value: "For-profit", label: "For-profit" },
  { value: "School", label: "School" },
  { value: "Government", label: "Government" },
  { value: "Other", label: "Other" },
];

const PROVIDER_SCOPES = [
  { value: "Local", label: "Local" },
  { value: "Regional", label: "Regional" },
  { value: "National", label: "National" },
  { value: "International", label: "International" },
];

const ACTIVITY_CATEGORIES = [
  { id: "sports", name: "Sports", subcategories: ["Soccer", "Basketball", "Baseball/Softball", "Swimming", "Gymnastics", "Martial Arts", "Tennis", "Golf", "Ice/Roller Skating", "Rock Climbing", "General Fitness", "Multi-sport", "Other Sports"] },
  { id: "arts", name: "Arts", subcategories: ["Visual Arts", "Music", "Dance", "Theater/Drama", "Crafts", "Photography", "Creative Writing", "Other Arts"] },
  { id: "stem", name: "STEM", subcategories: ["Coding/Programming", "Robotics", "Science", "Math", "Engineering", "Game Development", "Other STEM"] },
  { id: "nature", name: "Nature & Outdoors", subcategories: ["Camping", "Hiking", "Gardening", "Farming/Agriculture", "Wildlife/Nature Study", "Scouts/Guides", "Other Nature"] },
  { id: "academic", name: "Academic", subcategories: ["Tutoring", "Test Prep", "Languages", "Reading/Writing", "History", "Other Academic"] },
  { id: "life-skills", name: "Life Skills", subcategories: ["Cooking/Baking", "Financial Literacy", "Sewing/Textiles", "Woodworking", "First Aid/CPR", "Other Life Skills"] },
  { id: "music-performing", name: "Music & Performing Arts", subcategories: ["Instrumental", "Vocal/Choir", "Band/Orchestra", "DJ/Music Production", "Other Music"] },
];

const CreateOrgModal = ({ isOpen, toggle, onSuccess, getAccessToken }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    description: "",
    website: "",
    city: "",
    state: "",
    country: "US",
    contactPhone: "",
    contactEmail: "",
    isCurated: false,
    isVerified: false,
    providerScope: "Local",
    primaryCategory: "",
    tags: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [categorySearch, setCategorySearch] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Organization name is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state.trim()) newErrors.state = "State is required";
    if (formData.website && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = "Website must start with http:// or https://";
    }
    if (formData.contactEmail && !formData.contactEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.contactEmail = "Please enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/v2/organizations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create organization");
      }

      const newOrg = await response.json();
      onSuccess(newOrg);
      toggle();
      // Reset form
      setFormData({
        name: "",
        type: "",
        description: "",
        website: "",
        city: "",
        state: "",
        country: "US",
        contactPhone: "",
        contactEmail: "",
        isCurated: false,
        isVerified: false,
        providerScope: "Local",
        primaryCategory: "",
        tags: [],
      });
    } catch (error) {
      console.error("Error creating organization:", error);
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredCategories = ACTIVITY_CATEGORIES.filter(
    (cat) =>
      cat.name.toLowerCase().includes(categorySearch.toLowerCase()) ||
      cat.subcategories.some((sub) =>
        sub.toLowerCase().includes(categorySearch.toLowerCase())
      )
  );

  const selectCategory = (categoryId) => {
    setFormData((prev) => ({ ...prev, primaryCategory: categoryId }));
    setCategorySearch("");
    setShowCategoryDropdown(false);
  };

  const selectedCategoryName = ACTIVITY_CATEGORIES.find(
    (c) => c.id === formData.primaryCategory
  )?.name;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" style={{ maxWidth: "800px" }}>
      <ModalHeader
        toggle={toggle}
        style={{
          background: "rgba(30, 30, 40, 0.95)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          color: "#fff",
        }}
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-indigo-400" />
          Create New Organization
        </div>
      </ModalHeader>

      <ModalBody
        style={{
          background: "rgba(20, 20, 30, 0.95)",
          color: "#e0e0e0",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {submitError && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400">
            <AlertCircle className="w-5 h-5" />
            {submitError}
          </div>
        )}

        {/* Basic Information Section */}
        <div className="mb-6">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-indigo-400" />
            Basic Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300 flex items-center gap-1">
                Organization Name <span className="text-red-400">*</span>
              </Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., MPSA Crush Soccer"
                className="bg-gray-800 border-gray-600 text-white"
                invalid={!!errors.name}
              />
              {errors.name && <div className="text-red-400 text-sm mt-1">{errors.name}</div>}
            </div>

            <div>
              <Label className="text-gray-300">Organization Type</Label>
              <Input
                type="select"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                className="bg-gray-800 border-gray-600 text-white"
              >
                {ORGANIZATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Input>
            </div>
          </div>

          <div className="mt-4">
            <Label className="text-gray-300 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Description
            </Label>
            <Input
              type="textarea"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe the organization..."
              rows={4}
              className="bg-gray-800 border-gray-600 text-white"
            />
            <div className="text-gray-500 text-sm mt-1">
              {formData.description.length} characters (50+ recommended for better visibility)
            </div>
          </div>
        </div>

        {/* Location Section */}
        <div className="mb-6">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-indigo-400" />
            Location <span className="text-red-400">*</span>
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-gray-300">City</Label>
              <Input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="e.g., Ann Arbor"
                className="bg-gray-800 border-gray-600 text-white"
                invalid={!!errors.city}
              />
              {errors.city && <div className="text-red-400 text-sm mt-1">{errors.city}</div>}
            </div>

            <div>
              <Label className="text-gray-300">State</Label>
              <Input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="e.g., MI"
                className="bg-gray-800 border-gray-600 text-white"
                invalid={!!errors.state}
              />
              {errors.state && <div className="text-red-400 text-sm mt-1">{errors.state}</div>}
            </div>

            <div>
              <Label className="text-gray-300">Country</Label>
              <Input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="e.g., US"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mb-6">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-indigo-400" />
            Contact Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Website
              </Label>
              <Input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://..."
                className="bg-gray-800 border-gray-600 text-white"
                invalid={!!errors.website}
              />
              {errors.website && <div className="text-red-400 text-sm mt-1">{errors.website}</div>}
            </div>

            <div>
              <Label className="text-gray-300 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Phone
              </Label>
              <Input
                type="text"
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>

            <div>
              <Label className="text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                placeholder="contact@example.com"
                className="bg-gray-800 border-gray-600 text-white"
                invalid={!!errors.contactEmail}
              />
              {errors.contactEmail && <div className="text-red-400 text-sm mt-1">{errors.contactEmail}</div>}
            </div>

            <div>
              <Label className="text-gray-300">Provider Scope</Label>
              <Input
                type="select"
                name="providerScope"
                value={formData.providerScope}
                onChange={handleInputChange}
                className="bg-gray-800 border-gray-600 text-white"
              >
                {PROVIDER_SCOPES.map((scope) => (
                  <option key={scope.value} value={scope.value}>
                    {scope.label}
                  </option>
                ))}
              </Input>
            </div>
          </div>
        </div>

        {/* Activity Category */}
        <div className="mb-6">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Tag className="w-4 h-4 text-indigo-400" />
            Primary Activity Category
          </h4>

          <div className="relative">
            <Input
              type="text"
              value={categorySearch}
              onChange={(e) => {
                setCategorySearch(e.target.value);
                setShowCategoryDropdown(true);
              }}
              onFocus={() => setShowCategoryDropdown(true)}
              placeholder={selectedCategoryName || "Search for activity category..."}
              className="bg-gray-800 border-gray-600 text-white"
            />

            {showCategoryDropdown && (
              <div
                className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                style={{ background: "rgba(30, 30, 40, 0.98)" }}
              >
                {filteredCategories.length === 0 ? (
                  <div className="p-3 text-gray-400">No categories found</div>
                ) : (
                  filteredCategories.map((category) => (
                    <div key={category.id}>
                      <div
                        className="px-4 py-2 text-white font-medium hover:bg-indigo-600/30 cursor-pointer border-b border-gray-700"
                        onClick={() => selectCategory(category.id)}
                      >
                        {category.name}
                      </div>
                      {category.subcategories.map((sub) => (
                        <div
                          key={`${category.id}-${sub}`}
                          className="px-6 py-1 text-gray-300 hover:bg-indigo-600/20 cursor-pointer text-sm"
                          onClick={() => selectCategory(category.id)}
                        >
                          └ {sub}
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {selectedCategoryName && (
            <div className="mt-2 flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle className="w-4 h-4" />
              Selected: {selectedCategoryName}
            </div>
          )}
        </div>

        {/* Status Toggles */}
        <div className="mb-4">
          <h4 className="text-white font-medium mb-4">Status</h4>

          <div className="flex flex-wrap gap-6">
            <Label check className="flex items-center gap-2 cursor-pointer">
              <Input
                type="checkbox"
                name="isCurated"
                checked={formData.isCurated}
                onChange={handleInputChange}
                className="w-5 h-5"
              />
              <span className="text-gray-300">Curated (featured)</span>
            </Label>

            <Label check className="flex items-center gap-2 cursor-pointer">
              <Input
                type="checkbox"
                name="isVerified"
                checked={formData.isVerified}
                onChange={handleInputChange}
                className="w-5 h-5"
              />
              <span className="text-gray-300">Verified</span>
            </Label>
          </div>
        </div>

        {/* Required Fields Note */}
        <div className="mt-6 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
          <p className="text-indigo-300 text-sm">
            <span className="text-red-400">*</span> Required fields
          </p>
        </div>
      </ModalBody>

      <ModalFooter
        style={{
          background: "rgba(30, 30, 40, 0.95)",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            border: "none",
          }}
        >
          {isSubmitting ? (
            <>
              <span className="animate-spin mr-2">⟳</span>
              Creating...
            </>
          ) : (
            <>
              <Building2 className="w-4 h-4 mr-2 inline" />
              Create Organization
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateOrgModal;
